// src/components/invoices/Invoices.jsx - Using GenericTable
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Filter, Plus, Calendar, DollarSign } from 'lucide-react';

// Import shadcn components
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";

// Import our custom components
import GenericTable from '../ui/table/GenericTable';
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hook
import { useInvoices } from '../../hooks/useInvoices';
import { useCustomers } from '../../hooks/useCustomers';

const Invoices = () => {
  const navigate = useNavigate();

  // Get data from hooks
  const {
    invoices,
    loading: invoicesLoading,
    deleteInvoice,
    isConnected
  } = useInvoices();

  const { getCustomerById } = useCustomers();

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter invoices based on search and status
  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];

    return invoices.filter(invoice => {
      // Search filtering
      const matchesSearch = !searchTerm || [
        invoice.invoice_number,
        invoice.id,
        invoice.description,
        invoice.notes
      ].some(field =>
        field && field.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Status filtering
      const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  // Helper function to get customer info
  const getCustomerInfo = (customerId) => {
    if (!customerId) {
      return {
        name: 'Unknown Customer',
        email: ''
      };
    }

    const customer = getCustomerById(customerId);

    if (!customer) {
      return {
        name: 'Loading...',
        email: ''
      };
    }

    return {
      name: customer.name || customer.full_name || customer.customer_name,
      email: customer.email || customer.email_address
    };
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(amount || 0));
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to get status badge variant and color
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return { variant: 'default', className: 'bg-emerald-500 hover:bg-emerald-600', label: 'Paid' };
      case 'sent':
        return { variant: 'secondary', className: 'bg-blue-500 hover:bg-blue-600 text-white', label: 'Sent' };
      case 'overdue':
        return { variant: 'destructive', label: 'Overdue' };
      case 'partially_paid':
        return { variant: 'secondary', className: 'bg-amber-500 hover:bg-amber-600 text-white', label: 'Partial' };
      case 'draft':
        return { variant: 'outline', label: 'Draft' };
      case 'cancelled':
        return { variant: 'destructive', className: 'bg-gray-500 hover:bg-gray-600', label: 'Cancelled' };
      default:
        return { variant: 'outline', label: status || 'Unknown' };
    }
  };

  // Column definitions for invoices
  const columns = [
    {
      key: 'invoice_number',
      header: 'Invoice #',
      render: (invoice) => (
        <div className="flex items-center space-x-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">
            #{invoice.invoice_number || invoice.id}
          </span>
        </div>
      ),
      skeleton: (
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-[100px]" />
        </div>
      )
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (invoice) => {
        const customerInfo = getCustomerInfo(invoice.customer_id);
        return (
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">
              {customerInfo.name || 'Unknown Customer'}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {customerInfo.email}
            </p>
          </div>
        );
      },
      skeleton: (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-3 w-[100px]" />
        </div>
      )
    },
    {
      key: 'date',
      header: 'Date',
      render: (invoice) => (
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-foreground">
            {formatDate(invoice.invoice_date || invoice.created_at)}
          </span>
        </div>
      ),
      skeleton: <Skeleton className="h-4 w-[80px]" />
    },
    {
      key: 'due_date',
      header: 'Due Date',
      render: (invoice) => (
        <span className="text-foreground">
          {formatDate(invoice.due_date)}
        </span>
      ),
      skeleton: <Skeleton className="h-4 w-[80px]" />
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (invoice) => (
        <div className="flex items-center space-x-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">
            {formatCurrency(invoice.total || invoice.total_amount)}
          </span>
        </div>
      ),
      skeleton: <Skeleton className="h-4 w-[70px]" />
    },
    {
      key: 'status',
      header: 'Status',
      render: (invoice) => {
        const statusBadge = getStatusBadge(invoice.status);
        return (
          <Badge
            variant={statusBadge.variant}
            className={statusBadge.className}
          >
            {statusBadge.label}
          </Badge>
        );
      },
      skeleton: <Skeleton className="h-6 w-[60px] rounded-full" />
    }
  ];

  // Event handlers
  const handleRowClick = (invoice) => {
    navigate(`/invoices/${invoice.id}`);
  };

  const handleDelete = (invoice) => {
    deleteInvoice(invoice.id);
  };

  const getDeleteConfirmMessage = (invoice) => {
    const customerInfo = getCustomerInfo(invoice.customer_id);
    const invoiceNumber = invoice.invoice_number || invoice.id;
    return `Are you sure you want to delete invoice #${invoiceNumber} for ${customerInfo.name}? This action cannot be undone.`;
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/create');
  };

  // Empty state configuration
  const emptyState = {
    icon: FileText,
    title: searchTerm || statusFilter !== 'all'
      ? 'No matching invoices'
      : 'No invoices yet',
    description: searchTerm || statusFilter !== 'all'
      ? 'Try adjusting your search or filter criteria.'
      : 'Get started by creating your first invoice.',
    action: (!searchTerm && statusFilter === 'all') ? {
      icon: Plus,
      label: 'Create First Invoice',
      onClick: handleCreateInvoice
    } : null
  };

  // Connection error
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

      </div>

      {/* Generic Table */}
      <GenericTable
        data={filteredInvoices}
        columns={columns}
        loading={invoicesLoading && invoices.length === 0}
        emptyState={emptyState}
        onRowClick={handleRowClick}
        onDelete={handleDelete}
        deleteConfirmMessage={getDeleteConfirmMessage}
        showActionsColumn={false}
      />

      {/* Results Summary */}
      {filteredInvoices.length > 0 && (
        <div className="text-center text-muted-foreground text-sm mt-6">
          Showing {filteredInvoices.length} of {invoices?.length || 0} invoices
          {invoicesLoading && (
            <span className="ml-2">
              <span className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mr-1" />
              Updating...
            </span>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default Invoices;
